import { removeHtmlTag } from '@exam-management/common/utils';
import { FormLeavingListener } from '@exam-management/components';
import { useCreateItemSetQuestionGroupMutation } from '@exam-management/graphql';
import {
  QBankStructureLevelCategory,
  type QBankStructureLevel,
} from '@exam-management/qbs-provider';
import {
  type ItemSetCreationFormInput,
  getLocationDefaultValue,
  getLocationSchema,
} from '@exam-management/qcontent-management';
import { parseContentForApi } from '@exam-management/rich-text-editor';
import { useNavigate, useParams } from '@exam-management/router';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import type { PropsWithChildren } from '@shared/utils';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

const ITEM_SET_NAME_MAX_LENGTH = 256;

const DEFAULT_ITEM_SET_FORM_VALUES = {
  details: {
    name: '',
    paragraph: '',
  },
};

interface ItemSetQuestionCreationFormProps extends PropsWithChildren {
  qbsLevels: QBankStructureLevel[];
}

export default function ItemSetQuestionCreationForm(
  props: Readonly<ItemSetQuestionCreationFormProps>,
) {
  const { children, qbsLevels } = props;

  const { structureId } = useParams('/qbank-structures/:structureId');
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const [createItemSetQuestionGroup] = useCreateItemSetQuestionGroupMutation({
    onCompleted: (itemSetData) => {
      navigate(`/qbank-structures/:structureId/qcontents/item-set/:questionId`, {
        params: {
          structureId: structureId,
          questionId: itemSetData.qctCreateItemSet.id,
        },
      });
      showSnackbar({ variant: 'success', message: 'Item Set created successfully' });
    },
    onError: () => {
      showSnackbar({ variant: 'error', message: 'Upload failed. Please try again.' });
    },
  });

  const defaultLocationValues = getLocationDefaultValue(qbsLevels);

  const CreateItemSetSchema = z.object({
    details: z.object({
      name: z
        .string()
        .trim()
        .min(1, { message: 'กรุณากรอกชื่อ item set' })
        .max(ITEM_SET_NAME_MAX_LENGTH, {
          message: `ชื่อ item set ต้องมีความยาวไม่เกิน ${ITEM_SET_NAME_MAX_LENGTH} ตัวอักษร`,
        }),
      paragraph: z.string().refine(
        (value) => {
          const details = removeHtmlTag(value, {
            customTag: 'p',
          }).trim();
          return details.length >= 1;
        },
        {
          message: 'กรุณากรอกบทความ',
        },
      ),
    }),
    location: z.object({
      [QBankStructureLevelCategory.Part]: getLocationSchema(
        QBankStructureLevelCategory.Part,
        qbsLevels,
      ),
      [QBankStructureLevelCategory.Subject]: getLocationSchema(
        QBankStructureLevelCategory.Subject,
        qbsLevels,
      ),
      [QBankStructureLevelCategory.Group]: getLocationSchema(
        QBankStructureLevelCategory.Group,
        qbsLevels,
      ),
      [QBankStructureLevelCategory.Chapter]: getLocationSchema(
        QBankStructureLevelCategory.Chapter,
        qbsLevels,
      ),
      [QBankStructureLevelCategory.Topic]: getLocationSchema(
        QBankStructureLevelCategory.Topic,
        qbsLevels,
      ),
      [QBankStructureLevelCategory.SubTopic]: getLocationSchema(
        QBankStructureLevelCategory.SubTopic,
        qbsLevels,
      ),
    }),
  });

  const formContext = useForm<ItemSetCreationFormInput>({
    defaultValues: {
      ...DEFAULT_ITEM_SET_FORM_VALUES,
      location: {
        ...defaultLocationValues,
      },
    },
    resolver: zodResolver(CreateItemSetSchema),
  });

  const handleItemSetCreationFormSubmit = async (data: ItemSetCreationFormInput) => {
    const qBankStructureLevelItemId = data.location.SUB_TOPIC?.value as string;

    await createItemSetQuestionGroup({
      variables: {
        input: {
          name: data.details.name,
          paragraph: parseContentForApi(data.details.paragraph),
          qBankStructureLevelItemId: qBankStructureLevelItemId,
          refQBankStructureId: structureId,
        },
      },
    });
  };

  return (
    <FormProvider {...formContext}>
      <Form onSubmit={formContext.handleSubmit(handleItemSetCreationFormSubmit)}>{children}</Form>
      <FormLeavingListener onSubmit={handleItemSetCreationFormSubmit} />
    </FormProvider>
  );
}
